import React from 'react';
import 'katex/dist/katex.min.css';
import { InlineMath, BlockMath } from 'react-katex';

// ── Parse a markdown table block into rows ────────────────────────────────────
function parseTable(lines) {
  const rows = lines
    .filter(l => !/^\s*\|[-:\s|]+\|\s*$/.test(l))  // drop separator rows
    .map(l =>
      l.replace(/^\||\|$/g, '')   // strip leading/trailing pipes
       .split('|')
       .map(c => c.trim())
    );
  if (rows.length === 0) return null;
  const [header, ...body] = rows;
  return (
    <div style={{ overflowX: 'auto', margin: '12px 0' }}>
      <table style={{
        borderCollapse: 'collapse', width: '100%', fontSize: 13,
        border: '1px solid #d0d0d0', borderRadius: 6, overflow: 'hidden',
      }}>
        <thead>
          <tr>
            {header.map((cell, i) => (
              <th key={i} style={{
                padding: '8px 12px', background: '#f5f5f5', fontWeight: 600,
                border: '1px solid #d0d0d0', textAlign: 'left', whiteSpace: 'nowrap',
              }}>
                <InlineContent text={cell} />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {body.map((row, ri) => (
            <tr key={ri} style={{ background: ri % 2 === 0 ? '#fff' : '#fafafa' }}>
              {row.map((cell, ci) => (
                <td key={ci} style={{ padding: '7px 12px', border: '1px solid #e8e8e8' }}>
                  <InlineContent text={cell} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Render inline content: math + bold ───────────────────────────────────────
function InlineContent({ text }) {
  if (!text) return null;
  const parts = [];
  const blockSplit = text.split(/\$\$([\s\S]*?)\$\$/g);
  blockSplit.forEach((seg, i) => {
    if (i % 2 === 1) {
      parts.push({ type: 'block', text: seg });
    } else {
      const inlineSplit = seg.split(/\$(.*?)\$/g);
      inlineSplit.forEach((inSeg, j) => {
        if (j % 2 === 1) parts.push({ type: 'inline', text: inSeg });
        else parts.push({ type: 'text', text: inSeg });
      });
    }
  });

  return (
    <>
      {parts.map((part, i) => {
        if (part.type === 'block') return <BlockMath key={i} math={part.text.trim()} />;
        if (part.type === 'inline') return <InlineMath key={i} math={part.text} />;
        const boldParts = part.text.split(/\*\*(.*?)\*\*/g);
        return (
          <span key={i}>
            {boldParts.map((bp, bi) =>
              bi % 2 === 1 ? <strong key={bi}>{bp}</strong> : <span key={bi}>{bp}</span>
            )}
          </span>
        );
      })}
    </>
  );
}

// ── Main renderer ─────────────────────────────────────────────────────────────
export default function MathRenderer({ content, style }) {
  if (!content) return null;

  const lines = String(content).split('\n');
  const segments = [];  // array of { type: 'table'|'text', lines: [] }
  let current = null;

  for (const line of lines) {
    const isTableRow = /^\s*\|/.test(line);
    if (isTableRow) {
      if (current?.type !== 'table') { current = { type: 'table', lines: [] }; segments.push(current); }
      current.lines.push(line);
    } else {
      if (current?.type !== 'text') { current = { type: 'text', lines: [] }; segments.push(current); }
      current.lines.push(line);
    }
  }

  return (
    <span style={{ lineHeight: 1.8, display: 'inline-block', width: '100%', ...style }}>
      {segments.map((seg, si) => {
        if (seg.type === 'table') {
          return <React.Fragment key={si}>{parseTable(seg.lines)}</React.Fragment>;
        }

        // Text segment — handle block math, inline math, bold, line breaks
        const text = seg.lines.join('\n');
        const blockSplit = text.split(/\$\$([\s\S]*?)\$\$/g);
        return (
          <span key={si}>
            {blockSplit.map((chunk, bi) => {
              if (bi % 2 === 1) {
                return (
                  <div key={bi} style={{ margin: '8px 0', overflowX: 'auto' }}>
                    <BlockMath math={chunk.trim()} />
                  </div>
                );
              }
              const inlineSplit = chunk.split(/\$(.*?)\$/g);
              return (
                <span key={bi}>
                  {inlineSplit.map((inSeg, ii) => {
                    if (ii % 2 === 1) return <InlineMath key={ii} math={inSeg} />;
                    return (
                      <span key={ii}>
                        {inSeg.split('\n').map((line, li, arr) => {
                          const boldParts = line.split(/\*\*(.*?)\*\*/g);
                          return (
                            <span key={li}>
                              {boldParts.map((bp, bpi) =>
                                bpi % 2 === 1 ? <strong key={bpi}>{bp}</strong> : <span key={bpi}>{bp}</span>
                              )}
                              {li < arr.length - 1 && <br />}
                            </span>
                          );
                        })}
                      </span>
                    );
                  })}
                </span>
              );
            })}
          </span>
        );
      })}
    </span>
  );
}
